/**
 * ч.1299: вибір документів, що вже лежать на сервері, для копій у зверненні щодо зупиненої
 * ПН/РК. Спершу — вкладення документів ланцюга (сама ПН, рахунок, реалізація, ТТН, договір),
 * далі — решта вкладень організації з пошуком за іменем файла. Модальне вікно — через
 * BodyPortal (відкривається і з рейки, Закон дизайну #10).
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { erpnAppeals, type ServerAttachment } from '@/api/erpnAppeals';
import BodyPortal from './BodyPortal';
import { relatedDocName } from './RelatedDocs';
import TchTable from '@/shared/components/TchTable';

// Типи, яких немає серед звʼязаних документів (relatedDocName віддає сам код).
const EXTRA_TYPE_NAMES: Record<string, string> = {
  'doc.erpn_appeals': 'Звернення щодо зупиненої ПН/РК',
  'ref.contracts': 'Договір',
};

export function serverDocLabel(a: Pick<ServerAttachment, 'document_type' | 'order_kind' | 'doc_number' | 'doc_date'>): string {
  const raw = relatedDocName({ document_type: a.document_type, order_kind: a.order_kind });
  const name = raw === a.document_type ? (EXTRA_TYPE_NAMES[a.document_type] ?? raw) : raw;
  const num = a.doc_number ? ` № ${a.doc_number}` : '';
  const dt = a.doc_date ? ` від ${new Date(a.doc_date).toLocaleDateString('uk-UA')}` : '';
  return `${name}${num}${dt}`;
}

const fmtSize = (b: number) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} МБ` : `${Math.max(1, Math.round(b / 1024))} КБ`);

export default function ServerDocsPicker({ organizationId, invoiceId, excludeAppealId, excludeIds, onPick, onClose }: {
  organizationId: string;
  invoiceId?: string;
  excludeAppealId?: string;
  /** Уже обрані / уже додані — не пропонувати вдруге. */
  excludeIds?: string[];
  onPick: (items: ServerAttachment[]) => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState('');
  const [sel, setSel] = useState<Record<string, ServerAttachment>>({});
  const listQ = useQuery({
    queryKey: ['erpn-server-docs', organizationId, invoiceId ?? '', excludeAppealId ?? '', q],
    queryFn: () => erpnAppeals.browseAttachments({
      organization_id: organizationId, invoice_id: invoiceId || undefined, q: q || undefined,
      exclude_appeal_id: excludeAppealId || undefined, limit: 150,
    }),
    enabled: !!organizationId,
  });
  const excluded = useMemo(() => new Set(excludeIds ?? []), [excludeIds]);
  const chain = (listQ.data?.chain ?? []).filter((a) => !excluded.has(a.id));
  const others = (listQ.data?.items ?? []).filter((a) => !excluded.has(a.id));
  const count = Object.keys(sel).length;
  const toggle = (a: ServerAttachment) =>
    setSel((s) => { const n = { ...s }; if (n[a.id]) delete n[a.id]; else n[a.id] = a; return n; });

  const rows = (items: ServerAttachment[]) => (
    <TchTable>
      <thead>
        <tr>
          <th style={{ width: 32 }}></th>
          <th>Документ</th>
          <th>Файл</th>
          <th style={{ width: 80 }}>Розмір</th>
          <th style={{ width: 100 }}>Додано</th>
        </tr>
      </thead>
      <tbody>
        {items.map((a) => (
          <tr key={a.id} style={{ cursor: 'pointer' }} onClick={() => toggle(a)}>
            <td><input type="checkbox" checked={!!sel[a.id]} onChange={() => toggle(a)} onClick={(e) => e.stopPropagation()} /></td>
            <td>{serverDocLabel(a)}</td>
            <td>{a.filename}</td>
            <td>{fmtSize(a.size_bytes)}</td>
            <td>{new Date(a.created_at).toLocaleDateString('uk-UA')}</td>
          </tr>
        ))}
      </tbody>
    </TchTable>
  );

  return (
    <BodyPortal>
      <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'var(--overlay)',
        display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
        <div className="card" style={{ width: 'min(860px, 96vw)', maxHeight: '84vh', display: 'flex',
          flexDirection: 'column', margin: 0 }} onClick={(e) => e.stopPropagation()}
          data-testid="server-docs-picker">
          <div className="toolbar" style={{ marginBottom: '0.5rem' }}>
            <h3 style={{ margin: 0 }}>Документи на сервері</h3>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="пошук за іменем файла"
              style={{ marginLeft: 'auto', width: 260 }} />
            <button type="button" onClick={onClose}>закрити</button>
          </div>
          <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginBottom: '0.5rem' }}>
            Копія файла додається до звернення; оригінал у своєму документі лишається. Приймаються PDF, JPG, PNG.
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {listQ.isLoading && <div style={{ color: 'var(--muted)' }}>завантаження…</div>}
            {!listQ.isLoading && chain.length > 0 && (<>
              <div style={{ fontWeight: 600, margin: '0.25rem 0' }}>Документи ланцюга ПН/РК</div>
              {rows(chain)}
            </>)}
            {!listQ.isLoading && (
              <div style={{ fontWeight: 600, margin: '0.75rem 0 0.25rem' }}>
                {chain.length > 0 ? 'Інші вкладення організації' : 'Вкладення організації'}
              </div>
            )}
            {!listQ.isLoading && (others.length > 0 ? rows(others) : (
              <div style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
                {q ? 'нічого не знайдено' : 'вкладень поки немає'}
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.75rem' }}>
            <button type="button" onClick={onClose}>скасувати</button>
            <button type="button" className="primary" disabled={count === 0}
              onClick={() => onPick(Object.values(sel))}>
              додати{count ? ` (${count})` : ''}
            </button>
          </div>
        </div>
      </div>
    </BodyPortal>
  );
}
